<template>
    <div ref="patientListDiv" :style="patientListContainerStyle">
        <template v-if="isMobileView && !selectMultiple">
            <MContainerColumn v-if="!showMobilePatientList && mobileSelectedRow" class="w-full gap-2 mobile-patient-details">
                <MContainerRow class="gap-4">
                    <MContainerGrow>
                        <MContainerColumn class="gap-1">
                            <span class="mobile-patient-selected-label">{{ $t("shared.patient") }}</span>
                            <span class="mobile-patient-selected-name">{{ getPatientDisplayName(mobileSelectedRow.patient) }}</span>
                        </MContainerColumn>
                    </MContainerGrow>
                    <MContainer class="gap-4 items-center cursor-pointer">
                        <Button icon="pi pi-search" rounded style="background: transparent; border: 0" @click="showPatientsOnMobile" />
                        <i :class="[mobileDetailsCollapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up']" style="font-size: 1.1rem" @click="toggleMobileDetails" />
                    </MContainer>
                </MContainerRow>
                <MContainerColumn v-if="!mobileDetailsCollapsed" class="gap-2 mt-2">
                    <MContainerRow class="mobile-patient-row">
                        <span class="mobile-patient-row-label">{{ $t("web-rg.dateOfBirth") }}</span>
                        <span class="mobile-patient-row-value">{{ formatPatientBirthDate(mobileSelectedRow.patient.dateOfBirth) }}</span>
                    </MContainerRow>
                    <MContainerRow class="mobile-patient-row">
                        <span class="mobile-patient-row-label">{{ $t("web-rg.lastModified") }}</span>
                        <span class="mobile-patient-row-value">{{ formatPatientLastModified(mobileSelectedRow.patient.lastModified) }}</span>
                    </MContainerRow>
                    <MContainerRow class="mobile-patient-row">
                        <span class="mobile-patient-row-label">
                            {{ mobileSelectedRow.patient.status ? $t("weblink.status") : $t("weblink.productionStep") }}
                        </span>
                        <MContainer class="items-center gap-2">
                            <span
                                v-if="mobileSelectedRow.patient.orderStatusColor"
                                class="mobile-patient-status-dot"
                                :style="{ backgroundColor: mobileSelectedRow.patient.orderStatusColor }" />
                            <span class="mobile-patient-row-value">{{ getPatientProductionStepInfo(mobileSelectedRow.patient) }}</span>
                        </MContainer>
                    </MContainerRow>
                    <Divider />
                    <MContainerRow class="justify-evenly">
                        <MUButton
                            :disabled="loading"
                            :label="$t('web-rg.importcreate')"
                            icon="pi pi-plus"
                            :iconAbove="true"
                            :background="false"
                            :iconFont="'1.3rem'"
                            :labelFont="'smaller'"
                            @click="emits('importClicked')" />
                        <MUButton
                            :disabled="loading"
                            :label="$t('shared.sharePatient')"
                            icon="pi pi-cloud-upload"
                            :iconAbove="true"
                            :background="false"
                            :iconFont="'1.3rem'"
                            :labelFont="'smaller'"
                            @click="emits('shareClicked')" />
                        <MUButton
                            :disabled="loading"
                            :label="$t('shared.edit')"
                            icon="pi pi-user-edit"
                            :iconAbove="true"
                            :background="false"
                            :iconFont="'1.3rem'"
                            :labelFont="'smaller'"
                            @click="emits('editClicked')" />
                        <MUButton
                            :disabled="loading"
                            :label="$t('shared.export')"
                            icon="pi pi-file-export"
                            :iconAbove="true"
                            :background="false"
                            :iconFont="'1.3rem'"
                            :labelFont="'smaller'"
                            @click="emits('exportClicked')" />
                        <MUButton
                            :disabled="loading"
                            :label="$t('shared.delete')"
                            icon="pi pi-trash"
                            :iconAbove="true"
                            :background="false"
                            :iconFont="'1.3rem'"
                            :labelFont="'smaller'"
                            @click="emits('deleteClicked')" />
                    </MContainerRow>
                </MContainerColumn>
            </MContainerColumn>
            <Listbox
                v-if="showMobilePatientList"
                v-model="selectedRow"
                :options="mobileTableRows"
                filter
                :filterPlaceholder="$t('shared.search')"
                :filter-fields="['searchText']"
                optionLabel="searchText"
                class="mt-2"
                @change="onMobileItemSelected">
                <template #option="slotProps">
                    <MContainerColumn class="w-full gap-2 mobile-patient-option">
                        <MContainerRow class="justify-between gap-2">
                            <MContainer class="items-center gap-2 min-w-0 w-full">
                                <RgPatientListStateComponent v-model:patient="slotProps.option.patient" :maxStateIconsCount="maxStateIconsCount" />
                                <span class="mobile-patient-option-name">{{ getPatientDisplayName(slotProps.option.patient) }}</span>
                            </MContainer>
                        </MContainerRow>
                        <MContainerRow class="mobile-patient-row">
                            <span class="mobile-patient-row-label">{{ $t("web-rg.dateOfBirth") }}</span>
                            <span class="mobile-patient-row-value">{{ formatPatientBirthDate(slotProps.option.patient.dateOfBirth) }}</span>
                        </MContainerRow>
                        <MContainerRow class="mobile-patient-row">
                            <span class="mobile-patient-row-label">{{ $t("web-rg.lastModified") }}</span>
                            <span class="mobile-patient-row-value">{{ formatPatientLastModified(slotProps.option.patient.lastModified) }}</span>
                        </MContainerRow>
                        <MContainerRow class="mobile-patient-row">
                            <span class="mobile-patient-row-label">{{ slotProps.option.patient.status ? $t("weblink.status") : $t("weblink.productionStep") }}</span>
                            <MContainer class="items-center gap-2">
                                <span
                                    v-if="slotProps.option.patient.orderStatusColor"
                                    class="mobile-patient-status-dot"
                                    :style="{ backgroundColor: slotProps.option.patient.orderStatusColor }" />
                                <span class="mobile-patient-row-value">{{ getPatientProductionStepInfo(slotProps.option.patient) }}</span>
                            </MContainer>
                        </MContainerRow>
                        <Divider />
                    </MContainerColumn>
                </template>
            </Listbox>
        </template>
        <DataTable
            v-else
            id="patienListDt"
            v-bind:globalFilterFields="['patient.patientName', 'patient.user']"
            v-bind:paginator="true"
            v-model:first="first"
            v-bind:rows="rowsPerPage"
            v-bind:value="tableRows"
            :lazy="true"
            :totalRecords="totalPatientCount"
            :metaKeySelection="metaKey"
            dataKey="rowKey"
            :scroll-direction="'both'"
            scroll-height="flex"
            filterDisplay="menu"
            responsiveLayout="scroll"
            :selectionMode="selectionMode"
            @rowSelect="onItemSelected"
            :selection="selectionValue"
            @update:selection="onSelectionUpdated"
            v-model:expandedRows="expandedRows"
            @page="onPageClicked($event)"
            @sort="onSortClicked($event)"
            class="p-datatable-flex-scrollable"
            style="height: 100% !important; width: 100% !important; min-height: 0"
            tableStyle="padding-left: 1rem; padding-right: 1rem; w-full; ">
            <template v-slot:empty>{{ $t("web-rg.noPatientsFound") }}</template>
            <template v-slot:loading>{{ $t("web-rg.loadingPatientsDataPleaseWait") }}</template>
            <Column v-bind:sortable="false" field="patient.unreadNotifications" header="State" class="justify-center" :style="stateColumnStyle">
                <template #header>
                    <MContainer class="justify-center" />
                </template>
                <template v-slot:body="{ data }">
                    <RgPatientListStateComponent v-model:patient="data.patient" :maxStateIconsCount="maxStateIconsCount" />
                </template>
            </Column>
            <Column field="patient.firstName" sortable v-bind:header="$t('shared.name')">
                <template v-slot:body="{ data }">
                    <MContainer class="flex-row gap-1">
                        <RgPatientListCardInfoItem :label="$t('shared.name')" :value="`${data.patient.firstName} ${data.patient.lastName}`" />
                    </MContainer>
                </template>
            </Column>
            <Column field="patient.dateOfBirth" sortable v-bind:header="$t('web-rg.dateOfBirth')">
                <template v-slot:body="{ data }">
                    <RgPatientListCardInfoItem :label="$t('web-rg.dateOfBirth')" :value="formatPatientBirthDate(data.patient.dateOfBirth)" />
                </template>
            </Column>
            <Column field="patient.lastModified" sortable v-bind:header="$t('web-rg.lastModified')">
                <template v-slot:body="{ data }">
                    <RgPatientListCardInfoItem :label="$t('web-rg.lastModified')" :value="data.patient.lastModified.toLocaleDateString()" />
                </template>
            </Column>
            <Column field="patient.status" sortable v-bind:header="$t('weblink.productionStep')" :style="columnMinWidthStyle">
                <template v-slot:body="{ data }">
                    <RgPatientListCardInfoItem
                        :label="data.patient.status ? $t('weblink.status') : $t('weblink.productionStep')"
                        :value="getProductionStepInfo(data)"
                        :color="data.patient.orderStatusColor" />
                </template>
            </Column>
            <template #expansion="slotProps">
                <Divider />
                <MContainerRow class="justify-evenly">
                    <MUButton
                        :disabled="loading"
                        :label="$t('web-rg.importcreate')"
                        icon="pi pi-plus"
                        :iconAbove="true"
                        :background="false"
                        :iconFont="'1.3rem'"
                        :labelFont="'smaller'"
                        v-on:click="emits('importClicked')" />
                    <MUButton
                        :disabled="loading"
                        :label="$t('shared.sharePatient')"
                        icon="pi pi-cloud-upload"
                        :iconAbove="true"
                        :background="false"
                        :iconFont="'1.3rem'"
                        :labelFont="'smaller'"
                        v-on:click="emits('shareClicked')" />
                    <MUButton
                        :disabled="loading"
                        :label="$t('shared.edit')"
                        icon="pi pi-user-edit"
                        :iconAbove="true"
                        :background="false"
                        :iconFont="'1.3rem'"
                        :labelFont="'smaller'"
                        v-on:click="emits('editClicked')" />
                    <MUButton
                        :disabled="loading"
                        :label="$t('shared.export')"
                        icon="pi pi-file-export"
                        :iconAbove="true"
                        :background="false"
                        :iconFont="'1.3rem'"
                        :labelFont="'smaller'"
                        v-on:click="emits('exportClicked')" />
                    <MUButton
                        :disabled="loading"
                        :label="$t('shared.delete')"
                        icon="pi pi-trash"
                        :iconAbove="true"
                        :background="false"
                        :iconFont="'1.3rem'"
                        :labelFont="'smaller'"
                        v-on:click="emits('deleteClicked')" />
                </MContainerRow>
            </template>
        </DataTable>
    </div>
</template>
<script lang="ts" setup>
import DataTable, { DataTablePageEvent, DataTableSortEvent } from "primevue/datatable";
import Column from "primevue/column";
import { useRouter } from "vue-router";
import { computed, toRefs, ref, watch, watchEffect, nextTick, onBeforeUnmount } from "vue";
import { NamedRoutes } from "@/router/WebRgRouter";
import { LoadPatientListSlimModel, PatientListModel } from "@/models/PatientListModel";
import { DataTableRowSelectEvent } from "primevue/datatable";
import { MContainer, MContainerColumn, MContainerGrow, MContainerRow, MUButton, useUtilStore } from "@xlabmedical/medic-web-framework";
import RgPatientListCardInfoItem from "@/components/RgPatientListCardInfoItem.vue";
import { formatPatientBirthDate, getVisibleIconCount } from "@/util/PatientListHelpers";
import { useResizeObserver, useWindowSize, watchDebounced } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { useConfirm } from "primevue/useconfirm";

const router = useRouter();
const confirm = useConfirm();
const { t } = useI18n();

type RowData = {
    patient: PatientListModel;
    rowKey: string;
};

interface Props {
    patients: PatientListModel[];
    selectedPatient: PatientListModel;
    totalPatientCount?: number;
    page?: number;
    autoSelectFirst?: boolean;
    loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    totalPatientCount: 0,
    page: 0,
    autoSelectFirst: true,
    loading: false,
});

const searchString = defineModel("searchString");
const selectMultiple = defineModel<boolean>("selectMultiple");
const selectedPatients = defineModel<PatientListModel[]>("selectedPatients", { default: () => [] });

const emits = defineEmits([
    "selectionChanged",
    "shareClicked",
    "importData",
    "editClicked",
    "exportClicked",
    "importClicked",
    "deleteClicked",
    "pageChange",
    "pageSizeChange",
    "searchChange",
    "sortChange",
    "mobile-selected-details-collapsed-change",
]);

const utilStore = useUtilStore();
const { patients, totalPatientCount } = toRefs(props);
const patientListDiv = ref<HTMLElement | null>(null);
const expandedRows = ref<Record<string, boolean>>({});
const selectedRowKey = ref<string | null>(null);
const selectedRow = ref<RowData | null>(null);
const selectedRows = ref<RowData[]>([]);
const patientListContainerSize = ref<{ width: number; height: number } | null>(null);
const overflowRowCap = ref<number | null>(null);
const overflowCheckFrameId = ref<number | null>(null);
const overflowCheckTimeoutId = ref<number | null>(null);
const metaKey = computed(() => !selectMultiple.value);
const first = ref(0);
const maxStateIconsCount = ref(0);
const isMobileView = computed(() => utilStore.isMobileView);
const showMobilePatientList = ref(true);
const mobileDetailsCollapsed = ref(true);
const mobileSelectedRow = computed(() => selectedRow.value);
const mobileSelectedDetailsCollapsed = computed(() => {
    return isMobileView.value && !selectMultiple.value && !showMobilePatientList.value && Boolean(mobileSelectedRow.value) && mobileDetailsCollapsed.value;
});
const patientListContainerStyle = computed(() => ({
    borderRadius: "var(--32, 32px)",
    margin: "0",
    background: "transparent",
    height: mobileSelectedDetailsCollapsed.value ? "auto" : "100%",
    minHeight: mobileSelectedDetailsCollapsed.value ? "auto" : "0",
    overflow: "hidden",
    paddingTop: "1rem !important",
}));

const selectionMode = computed(() => (selectMultiple.value ? "multiple" : "single"));
const selectionValue = computed(() => (selectMultiple.value ? selectedRows.value : selectedRow.value));

const selectionChanged = (row: RowData | null, emitEvent = true) => {
    if (selectMultiple.value) return;
    const newKey = row?.rowKey ?? null;
    const alreadySelected = selectedRowKey.value === newKey;

    selectedRowKey.value = newKey;
    selectedRow.value = row;
    selectedPatients.value = row ? [row.patient] : [];

    if (!selectMultiple.value && row) {
        expandedRows.value = { [row.rowKey]: true };
    }

    if (emitEvent && !alreadySelected) {
        // skip at login
        if (router.options.history.state.current !== "/patients") {
            emits("selectionChanged", row?.patient ?? null);
        }
    }
};

const onSelectionUpdated = (value: RowData | RowData[] | null) => {
    if (selectMultiple.value) {
        if (Array.isArray(value)) {
            selectedRows.value = value;
        } else if (value) {
            selectedRows.value = [value];
        } else {
            selectedRows.value = [];
        }
        if (selectedRows.value.length > 10) {
            selectedRows.value = selectedRows.value.slice(0, 10);
            confirm.require({
                group: "maxPatientsSelection",
                header: t("implant-planner.warning"),
                icon: "pi pi-exclamation-triangle",
                rejectLabel: t("shared.close"),
                rejectIcon: "pi pi-times",
                message: t("web-rg.maxPatientsSelectionMessage"),
                reject: () => {
                    confirm.close();
                },
            });
        }
        selectedPatients.value = selectedRows.value.map((row) => row.patient);
        return;
    }

    selectedRow.value = Array.isArray(value) ? value[0] ?? null : value;
    selectedPatients.value = selectedRow.value ? [selectedRow.value.patient] : [];
};

const onItemSelected = (e: DataTableRowSelectEvent) => {
    const row = e.data as RowData;
    selectionChanged(row, true);
};

const onMobileItemSelected = (event: { value?: RowData }) => {
    const row = event?.value ?? selectedRow.value;
    if (!row) {
        return;
    }

    onSelectionUpdated(row);
    selectionChanged(row, true);
    showMobilePatientList.value = false;
    mobileDetailsCollapsed.value = true;
};

const showPatientsOnMobile = () => {
    mobileDetailsCollapsed.value = true;
    showMobilePatientList.value = true;
};

const toggleMobileDetails = () => {
    mobileDetailsCollapsed.value = !mobileDetailsCollapsed.value;
};

const getPatientDisplayName = (patient: PatientListModel) => {
    const fullName = `${patient.firstName ?? ""} ${patient.lastName ?? ""}`.trim();
    return fullName || patient.patientName || "-";
};

const formatPatientLastModified = (date?: Date) => {
    return date ? new Date(date).toLocaleDateString() : "";
};

function urlReplacePatient(patient: PatientListModel) {
    if (router.currentRoute.value.name !== NamedRoutes.Patients) return;
    if (!patient || !patient.patientName) {
        console.warn("Invalid patient data for URL replacement");
        return;
    }

    if (router.currentRoute.value.params.id === patient.patientName) {
        return;
    }

    router.replace({
        name: NamedRoutes.Patients,
        params: { id: patient.patientName },
    });
}

watchDebounced(
    searchString,
    (value) => {
        emits("searchChange", value);
    },
    { debounce: 300, maxWait: 1000 },
);

watch(
    mobileSelectedDetailsCollapsed,
    (isCollapsed) => {
        emits("mobile-selected-details-collapsed-change", isCollapsed);
    },
    { immediate: true },
);

const { height, width } = useWindowSize();

const estimatedRowsPerPage = computed(() => {
    const h = height.value;

    const extraPenalty = Math.max(0, 900 - h) * 0.35;
    const overhead = 370 + extraPenalty;
    const rowH = h < 768 ? 92 : h < 1024 ? 86 : 80;

    let rows = Math.floor(((h - overhead) / rowH) * 1.75);

    if (height.value < 900) {
        return Math.max(rows, 1);
    }

    if (height.value < 950) {
        return Math.max(rows - 1, 1);
    }

    if (height.value < 1000) {
        return Math.max(rows, 1);
    }

    return Math.max(rows - 1, 1);
});

const rowsPerPage = computed(() => {
    const cap = overflowRowCap.value ?? estimatedRowsPerPage.value;
    return Math.max(1, Math.min(estimatedRowsPerPage.value, cap));
});

const columnMinWidthStyle = computed(() => (width.value < 2300 ? "min-width: 12rem !important;" : ""));
const STATE_ICON_SIZE_PX = 24;
const STATE_ICON_GAP_PX = 4;
const STATE_CELL_PADDING_X_PX = 28;
const STATE_COLUMN_MIN_WIDTH_PX = 88;

const stateColumnStyle = computed(() => {
    const iconCount = Math.max(maxStateIconsCount.value, 1);
    const iconsWidthPx = iconCount * STATE_ICON_SIZE_PX + Math.max(iconCount - 1, 0) * STATE_ICON_GAP_PX;
    const targetWidthPx = Math.max(STATE_COLUMN_MIN_WIDTH_PX, iconsWidthPx + STATE_CELL_PADDING_X_PX);
    const widthPx = `${targetWidthPx}px`;

    return { width: widthPx, minWidth: widthPx };
});

const createRowData = (patient: PatientListModel, localIndex: number): RowData => {
    const rowKey = patient.patientId ?? patient.uuid ?? patient.patientName ?? `row-${localIndex}`;
    return { patient, rowKey };
};

const tableRows = computed<RowData[]>(() => patients.value.map((patient, index) => createRowData(patient, index)));
const mobileTableRows = computed(() =>
    tableRows.value.map((row) => ({
        ...row,
        searchText: `${row.patient.patientName ?? ""} ${row.patient.firstName ?? ""} ${row.patient.lastName ?? ""} ${row.patient.status ?? ""} ${
            row.patient.lastProjectStep ?? ""
        }`,
    })),
);

const getDataTableOverflowContainer = () => {
    if (!patientListDiv.value) {
        return null;
    }

    const candidates = [
        ...Array.from(patientListDiv.value.querySelectorAll(".p-datatable-table-container")),
        ...Array.from(patientListDiv.value.querySelectorAll(".p-datatable-wrapper")),
        ...Array.from(patientListDiv.value.querySelectorAll(".p-datatable-scrollable-body")),
    ] as HTMLElement[];

    return candidates.find((candidate) => candidate.scrollHeight - candidate.clientHeight > 1) ?? candidates[0] ?? null;
};

const detectDataTableOverflowAndAdjustRows = () => {
    if (props.loading || rowsPerPage.value <= 1 || tableRows.value.length === 0) {
        return;
    }

    // Wait for a page that already matches requested page size before shrinking again.
    if (tableRows.value.length > rowsPerPage.value) {
        return;
    }

    const tableContainer = getDataTableOverflowContainer();
    if (!tableContainer) {
        return;
    }

    const overflowPx = tableContainer.scrollHeight - tableContainer.clientHeight;
    if (overflowPx <= 1) {
        return;
    }

    const rowElement = tableContainer.querySelector(".p-datatable-tbody > tr:not(.p-datatable-row-expansion):not(.p-datatable-emptymessage)") as HTMLElement | null;
    const rowHeight = rowElement?.getBoundingClientRect().height ?? 0;
    const rowsToReduce = rowHeight > 0 ? Math.ceil(overflowPx / rowHeight) : 1;
    const nextRowsCap = Math.max(rowsPerPage.value - rowsToReduce, 1);

    if (overflowRowCap.value === null || nextRowsCap < overflowRowCap.value) {
        overflowRowCap.value = nextRowsCap;
    }
};

const runOverflowDetection = () => {
    void nextTick().then(() => {
        detectDataTableOverflowAndAdjustRows();
    });
};

const scheduleOverflowDetection = () => {
    if (overflowCheckFrameId.value !== null) {
        cancelAnimationFrame(overflowCheckFrameId.value);
    }
    if (overflowCheckTimeoutId.value !== null) {
        clearTimeout(overflowCheckTimeoutId.value);
    }

    overflowCheckFrameId.value = window.requestAnimationFrame(() => {
        overflowCheckFrameId.value = null;
        runOverflowDetection();

        // Width-driven wrapping often settles one tick later; run a second pass.
        overflowCheckTimeoutId.value = window.setTimeout(() => {
            overflowCheckTimeoutId.value = null;
            runOverflowDetection();
        }, 140);
    });
};

useResizeObserver(patientListDiv, (entries) => {
    const entry = entries[0];
    if (!entry) {
        return;
    }

    const { width: nextWidth, height: nextHeight } = entry.contentRect;
    const previousSize = patientListContainerSize.value;
    patientListContainerSize.value = { width: nextWidth, height: nextHeight };

    const containerGrew = previousSize !== null && (nextWidth > previousSize.width + 1 || nextHeight > previousSize.height + 1);

    if (containerGrew && overflowRowCap.value !== null) {
        overflowRowCap.value = null;
    }

    scheduleOverflowDetection();
});

watch(
    width,
    (nextWidth, previousWidth) => {
        const grew = previousWidth !== undefined && nextWidth > previousWidth + 1;
        if (grew && overflowRowCap.value !== null) {
            overflowRowCap.value = null;
        }
        scheduleOverflowDetection();
    },
    { flush: "post" },
);

watch(
    () => tableRows.value,
    (rows) => {
        if (!selectedRowKey.value) return;
        const match = rows.find((row) => row.rowKey === selectedRowKey.value);
        if (match) {
            selectedRow.value = match;
            if (!selectMultiple.value) {
                expandedRows.value = { [match.rowKey]: true };
            }
        } else {
            selectedRow.value = null;
            selectedRowKey.value = null;
            expandedRows.value = {};
        }
    },
);

watchEffect(() => {
    let maxCount = 0;
    for (const patient of patients.value) {
        const count = getVisibleIconCount(patient);
        if (count > maxCount) maxCount = count;
    }
    maxStateIconsCount.value = maxCount;
});

watch(
    estimatedRowsPerPage,
    (rows, previousRows) => {
        if (previousRows !== undefined && previousRows !== rows) {
            overflowRowCap.value = null;
        }
        scheduleOverflowDetection();
    },
    { immediate: true },
);

watch(
    [() => tableRows.value.length, rowsPerPage, maxStateIconsCount, () => props.loading],
    () => {
        scheduleOverflowDetection();
    },
    { flush: "post" },
);

watch(
    expandedRows,
    () => {
        scheduleOverflowDetection();
    },
    { deep: true, flush: "post" },
);

watchEffect(() => {
    if (selectedRow.value?.patient) {
        urlReplacePatient(selectedRow.value.patient);
        return;
    }

    const selectedPatientInTable = props.selectedPatient && tableRows.value.some((row) => row.patient.patientName === props.selectedPatient.patientName);

    if (!props.autoSelectFirst) return;
    if (selectedPatientInTable) return;

    const firstRow = tableRows.value[0];
    if (firstRow) {
        selectionChanged(firstRow, true);
        urlReplacePatient(firstRow.patient);
    }
});

watch(
    rowsPerPage,
    (rows, previousRows) => {
        if (previousRows === undefined || previousRows !== rows) {
            emits("pageSizeChange", rows);
        }
        const currentPage = props.page ?? 0;
        const targetFirst = currentPage * rows;
        if (first.value !== targetFirst) {
            first.value = targetFirst;
        }
    },
    { immediate: true },
);

watch(
    () => props.page,
    (page = 0) => {
        const targetFirst = page * rowsPerPage.value;
        if (first.value !== targetFirst) {
            first.value = targetFirst;
        }
    },
    { immediate: true },
);

function getProductionStepInfo(item: RowData) {
    return getPatientProductionStepInfo(item.patient);
}

function getPatientProductionStepInfo(patient: PatientListModel) {
    const status = patient.status;
    if (status) {
        return status;
    }

    const step = patient.lastProjectStep;

    if (!step) {
        return "New Project";
    }
    return step;
}

const onPageClicked = (event: DataTablePageEvent) => {
    selectedRowKey.value = null;
    selectedRow.value = null;
    expandedRows.value = {};
    emits("pageChange", { page: event.page, rows: event.rows });
};

const onSortClicked = (event: DataTableSortEvent) => {
    const sortField = typeof event.sortField === "string" ? event.sortField : "";
    const sortColumnMap: Record<string, LoadPatientListSlimModel["sortColumn"]> = {
        "patient.firstName": "NAME",
        "patient.lastModified": "LAST_MODIFIED",
        "patient.dateOfBirth": "BIRTH_DATE",
        "patient.status": "LATEST_STEP",
    };

    const rows = rowsPerPage.value;
    const pageFromProps = props.page ?? 0;
    const currentFirst = pageFromProps * rows;
    const page = rows > 0 ? Math.floor(currentFirst / rows) : 0;
    const sortColumn = sortColumnMap[sortField] ?? "LAST_MODIFIED";
    const sortDirection: LoadPatientListSlimModel["sortDirection"] = event.sortOrder === 1 ? "ASC" : "DESC";

    emits("sortChange", { page, rows, sortColumn, sortDirection });
};

watch(
    () => props.selectedPatient,
    (newPatient) => {
        if (!newPatient) return;
        const localIndex = patients.value.findIndex((p) => p.patientName === newPatient.patientName);
        if (localIndex === -1) return;
        const row = createRowData(patients.value[localIndex], localIndex);
        selectionChanged(row, false);
    },
    { deep: true },
);

watch(
    () => selectMultiple.value,
    (isMulti) => {
        if (isMulti) {
            // Entering multi-select should start with no selected patients.
            selectedRows.value = [];
            selectedPatients.value = [];
            selectedRowKey.value = null;
            selectedRow.value = null;
            expandedRows.value = {};
            return;
        }

        // Switching back to single-select: always focus the first visible row; fallback to previous selections only when list is empty
        const targetRow = tableRows.value[0] ?? selectedRows.value[0] ?? selectedRow.value ?? null;

        selectedRows.value = [];

        if (!targetRow) {
            selectedPatients.value = [];
            expandedRows.value = {};
            return;
        }

        selectionChanged(targetRow, true);
        urlReplacePatient(targetRow.patient);
        selectedPatients.value = [targetRow.patient];
    },
);

watch(
    () => selectedRow.value,
    (row) => {
        if (!isMobileView.value || selectMultiple.value || row) {
            return;
        }

        showMobilePatientList.value = true;
        mobileDetailsCollapsed.value = true;
    },
);

onBeforeUnmount(() => {
    if (overflowCheckFrameId.value !== null) {
        cancelAnimationFrame(overflowCheckFrameId.value);
    }
    if (overflowCheckTimeoutId.value !== null) {
        clearTimeout(overflowCheckTimeoutId.value);
    }
});
</script>
<style scoped>
.p-datatable-flex-scrollable {
    display: flex;
    flex-direction: column;
    height: 100% !important;
    min-height: 0;
    background: transparent;
}

[data-pc-section="rowexpansioncell"] .p-divider-horizontal {
    margin-top: -0.9rem !important;
}

:deep(.p-datatable-row-expansion) {
    background: var(--p-primary-color) !important;
}

:deep(.p-datatable-row-selected) {
    background: var(--p-primary-color) !important;
}

:deep(.p-datatable .p-datatable-thead > tr > th),
:deep(.p-datatable .p-datatable-tbody > tr > td),
:deep(.p-datatable .p-datatable-tfoot > tr > td) {
    border-left: 0 !important;
    border-right: 0 !important;
    border-top: 0;
    border-bottom: 1px solid var(--surface-border);
}

:deep(.p-datatable .p-datatable-thead > tr > th:first-child),
:deep(.p-datatable .p-datatable-tbody > tr > td:first-child),
:deep(.p-datatable .p-datatable-tfoot > tr > td:first-child) {
    border: 0 !important;
}

:deep(.p-datatable-header) {
    padding-top: 1rem;
    border: 0 !important;
}

:deep(.p-datatable-table-container) {
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
    overflow: visible !important;
}

:deep(.p-datatable-header-cell) {
    height: 54px !important;
}

:deep(.p-datatable-thead) {
    background: transparent !important;
}

:deep(.p-datatable-row-selected:hover) {
    background: var(--p-primary-color) !important;
}

/* Final hard override for RG patient table header transparency */
:deep(#patienListDt),
:deep(#patienListDt .p-datatable-thead) {
    backdrop-filter: none !important;
}

/* Apply header background to first 5 columns */
:deep(#patienListDt .p-datatable-thead > tr > th:nth-child(-n + 5)),
:deep(#patienListDt .p-datatable-header-cell:nth-child(-n + 5)) {
    background: var(--p-rg-table-header-background-color) !important;
}

/* Left corner */
:deep(#patienListDt .p-datatable-thead > tr > th:nth-child(1)),
:deep(#patienListDt .p-datatable-header-cell:nth-child(1)) {
    border-top-left-radius: 16px !important;
}

/* Right corner */
:deep(#patienListDt .p-datatable-thead > tr > th:nth-child(5)),
:deep(#patienListDt .p-datatable-header-cell:nth-child(5)) {
    border-top-right-radius: 16px !important;
}

.mobile-patient-details {
    padding: 0.9rem 0 1rem 0;
}

.mobile-patient-selected-label {
    font-size: 0.86rem;
    opacity: 0.9;
}

.mobile-patient-selected-name {
    font-size: 1.25rem;
    line-height: 1.5rem;
    margin-top: 0.2rem;
}

.mobile-patient-option {
    width: 100%;
}

.mobile-patient-option-name {
    font-weight: 600;
    white-space: nowrap;
    max-width: max(100%, 25ch);
    text-overflow: ellipsis;
}

.mobile-patient-row {
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
}

.mobile-patient-row-label {
    opacity: 0.72;
}

.mobile-patient-row-value {
    text-align: right;
    font-weight: 500;
}

.mobile-patient-status-dot {
    width: 10px;
    height: 10px;
    border-radius: 999px;
}
</style>
